<template>
  <div class="text-gray-800 tabular-nums">
    <div class="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2">
      <div class="flex items-center gap-2">
        <span class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Show</span>
        <div class="inline-flex overflow-hidden rounded-md border border-gray-300 text-[13px]">
          <button
            v-for="f in FILTERS"
            :key="f[0]"
            class="px-2.5 py-1"
            :class="filter === f[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="filter = f[0]"
          >{{ f[1] }}</button>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Sort</span>
        <div class="inline-flex overflow-hidden rounded-md border border-gray-300 text-[13px]">
          <button
            v-for="s in SORTS"
            :key="s[0]"
            class="px-2.5 py-1"
            :class="sort === s[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="sort = s[0]"
          >{{ s[1] }}</button>
        </div>
      </div>
      <span v-if="!loading && rows.length" class="ml-auto text-[11px] text-gray-500">{{ rows.length }} practices · {{ totalPrioritized }} prioritized</span>
    </div>

    <div v-if="loading" class="py-10 text-center text-[13px] text-gray-500">Loading…</div>
    <div v-else-if="!entity_id" class="py-10 text-center text-[13px] text-gray-500">No linked entity for this user.</div>
    <div v-else-if="!rows.length" class="py-10 text-center text-[13px] text-gray-500">{{ emptyMessage }}</div>

    <div v-else class="h-[500px] overflow-auto rounded-lg border border-gray-200">
      <table class="w-full border-collapse text-[13px]">
        <thead>
          <tr class="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-500">
            <th class="py-2 pl-4 pr-2 text-left">Practice</th>
            <th class="px-2 py-2 text-right" title="Open constraints at this practice">Open constraints</th>
            <th class="px-2 py-2 text-right" title="High-severity open constraints">High severity</th>
            <th class="px-2 py-2 text-right" title="Open action items">Open actions</th>
            <th class="px-2 py-2 text-right" title="Open items flagged for the next practice meeting">Prioritized</th>
            <th class="px-2 pr-4 py-2 text-right" title="Next scheduled meeting, or the expected next touch from meeting cadence">Next meeting</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="p in rows" :key="p.id">
            <tr class="cursor-pointer border-b border-gray-100 hover:bg-gray-50" @click="toggle(p.id)">
              <td class="py-2 pl-4 pr-2">
                <div class="flex items-center gap-2">
                  <span class="w-3 flex-none text-[11px] text-gray-400">{{ openRows[p.id] ? '▾' : '▸' }}</span>
                  <span class="sensitive font-medium text-gray-900">{{ p.name }}</span>
                  <router-link
                    :to="`/entity/${p.id}`"
                    class="ml-1 text-[11px] font-medium text-blue-600 no-underline hover:underline"
                    title="Open practice page"
                    @click.stop
                  >View</router-link>
                </div>
              </td>
              <td class="px-2 py-2 text-right font-semibold text-gray-900">{{ p.c_open }}</td>
              <td class="px-2 py-2 text-right font-semibold" :class="p.c_high ? 'text-red-600' : 'text-gray-300'">{{ p.c_high }}</td>
              <td class="px-2 py-2 text-right font-semibold text-gray-900">{{ p.a_open }}</td>
              <td class="px-2 py-2 text-right font-semibold" :class="p.prioritized_count ? 'text-amber-600' : 'text-gray-300'">{{ p.prioritized_count }}</td>
              <td class="px-2 pr-4 py-2 text-right whitespace-nowrap">
                <span class="inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="dueClass(p)">{{ dueLabel(p) }}</span>
              </td>
            </tr>
            <tr v-if="openRows[p.id]" class="bg-gray-50/50">
              <td colspan="6" class="px-4 py-1">
                <div v-if="!displayItems(p).length" class="py-2 text-[12px] text-gray-400">Nothing {{ filter === 'priority' ? 'prioritized' : 'open' }} here.</div>
                <div
                  v-for="item in displayItems(p)"
                  :key="item.id"
                  class="flex items-start gap-2 border-b border-gray-100 py-2 last:border-0 hover:bg-white"
                >
                  <button
                    type="button"
                    class="mt-0.5 shrink-0 inline-flex min-w-[6.5rem] items-center justify-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors"
                    :class="item.prioritized ? 'border-amber-200 bg-amber-100 text-amber-800' : 'border-gray-300 bg-white text-gray-500 hover:border-amber-300 hover:text-amber-700'"
                    :title="priorityTitle(item)"
                    @click.stop="togglePriority(item)"
                  ><font-awesome-icon :icon="['fas', 'star']" class="text-[9px]" />{{ item.prioritized ? 'Priority' : 'Prioritize' }}</button>
                  <font-awesome-icon
                    :icon="['fas', item.kind === 'constraint' ? 'ban' : 'square-check']"
                    class="mt-1 shrink-0 text-[11px]"
                    :class="item.kind === 'constraint' ? 'text-amber-500' : 'text-blue-500'"
                    :title="item.kind === 'constraint' ? 'Constraint' : 'Action item'"
                  />
                  <div class="min-w-0 flex-1">
                    <button
                      type="button"
                      class="sensitive block w-full text-left font-medium leading-snug text-gray-800 hover:underline"
                      data-demo-blur
                      @click.stop="openDetail(item, p)"
                    >{{ item.title }}</button>
                    <div class="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                      <span
                        v-if="item.severity"
                        class="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        :style="severityStyle(item.severity)"
                      >{{ item.severity }}</span>
                      <span
                        v-if="item.overdue"
                        class="inline-flex items-center rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700"
                      >Overdue</span>
                      <span v-if="item.owner" class="sensitive">{{ item.owner }}</span>
                      <span v-if="item.initiative">{{ initiativeLabel(item.initiative) }}</span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <teleport to="body">
      <div
        v-if="detail.open"
        class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-800/60 p-4"
        @click.self="closeDetail"
      >
        <div class="relative mt-12 w-full max-w-2xl rounded-xl bg-white shadow-xl">
          <div class="flex items-center justify-end border-b border-gray-100 px-3 py-2">
            <button type="button" class="text-gray-400 hover:text-gray-600" title="Close" @click="closeDetail">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
          <div class="max-h-[80vh] overflow-y-auto">
            <div v-if="detail.loading" class="p-8 text-center text-sm text-gray-500">Loading…</div>
            <div v-else-if="detail.error" class="p-8 text-center text-sm text-red-600">{{ detail.error }}</div>
            <ET_NodeDetail
              v-else-if="selectedNode"
              density="full"
              :node="selectedNode"
              :edges="detail.edges"
              :title-by-id="titleById"
              :all-nodes="detail.nodes"
              :metric-names="metricNames"
              :saving="saving"
              @select="setSelected"
              @change-status="changeStatus"
              @accept-llm="acceptLlm"
              @patch-field="patchField"
              @patch-notes="patchNotes"
              @toggle-priority="detailTogglePriority"
              @open-transcript="onOpenTranscript"
              @open-node="(n) => setSelected(n && typeof n === 'object' ? n.id : n)"
            />
            <div v-else class="p-8 text-center text-sm text-gray-500">Item not found in the practice graph.</div>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <TranscriptModal
        :open="transcript.open"
        :engagement-id="transcript.engagementId"
        :quote="transcript.quote"
        @close="transcript.open = false"
      />
    </teleport>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faStar, faBan, faSquareCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getNextMeetings, setPriority } from '@/services/api/engagementGraph';
import { severityStyle } from '@/components/EngagementTracker/engagementTrackerHelpers.js';
import ET_NodeDetail from '@/components/EngagementTracker/parts/ET_NodeDetail.vue';
import TranscriptModal from '@/components/Details/TranscriptModal.vue';
import { initiativeLabel, dueLabel, dueClass } from '@/components/ConfigurablePageComponents/practiceWorkHelpers.js';

library.add(faStar, faBan, faSquareCheck, faXmark);

const FILTERS = [['all', 'All'], ['priority', 'Priority only']];
const SORTS = [['upcoming', 'Upcoming'], ['name', 'Alphabetical']];

export default {
  name: 'PracticeConstraints_Table',
  components: { FontAwesomeIcon, ET_NodeDetail, TranscriptModal },
  props: {
    entity_id: { type: Number, default: null },
    sectionId: { type: String, default: null },
    expanded: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true, practices: [], filter: 'all', sort: 'upcoming', openRows: {}, FILTERS, SORTS,
      detail: { open: false, loading: false, error: null, practiceId: null, nodes: [], edges: [], selectedId: null },
      saving: new Set(),
      metricNames: {},
      transcript: { open: false, engagementId: null, quote: '' },
    };
  },
  computed: {
    rows() {
      let list = this.practices.map(p => {
        const items = p.items || [];
        return {
          ...p,
          c_open: items.filter(i => i.kind === 'constraint').length,
          c_high: items.filter(i => i.kind === 'constraint' && i.severity === 'high').length,
          a_open: items.filter(i => i.kind === 'action').length,
          prioritized_count: items.filter(i => i.prioritized).length,
        };
      });
      if (this.filter === 'priority') list = list.filter(p => p.prioritized_count > 0);
      if (this.sort === 'name') list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      return list;
    },
    totalPrioritized() {
      return this.rows.reduce((n, p) => n + p.prioritized_count, 0);
    },
    emptyMessage() {
      if (this.filter === 'priority') return 'Nothing prioritized for your practices yet.';
      return 'No open work at the practices you manage.';
    },
    nodeById() {
      const m = {};
      for (const n of this.detail.nodes) m[n.id] = n;
      return m;
    },
    titleById() {
      const m = {};
      for (const n of this.detail.nodes) m[n.id] = n.title;
      return m;
    },
    selectedNode() {
      return this.detail.selectedId != null ? (this.nodeById[this.detail.selectedId] || null) : null;
    },
  },
  watch: {
    entity_id() { this.load(); },
  },
  mounted() { this.load(); },
  methods: {
    initiativeLabel,
    dueLabel,
    dueClass,
    severityStyle,
    priorityTitle(item) {
      if (!item.prioritized) return 'Prioritize for next meeting';
      return item.prioritized_by ? `Prioritized by ${item.prioritized_by}` : 'Prioritized for next meeting';
    },
    async togglePriority(item) {
      const data = await setPriority(item.id, !item.prioritized);
      item.prioritized = data.prioritized_at != null;
      item.prioritized_at = data.prioritized_at;
      item.prioritized_by = data.prioritized_by_name;
    },
    async load() {
      if (!this.entity_id) { this.practices = []; this.loading = false; return; }
      this.loading = true;
      const requested = this.entity_id;
      const data = await getNextMeetings(requested);
      if (requested !== this.entity_id) return;
      this.practices = data.practices || [];
      this.loading = false;
    },
    displayItems(p) {
      return this.filter === 'priority' ? (p.items || []).filter(i => i.prioritized) : (p.items || []);
    },
    toggle(id) { this.openRows[id] = !this.openRows[id]; },
    async openDetail(item, p) {
      this.detail = { open: true, loading: true, error: null, practiceId: p.id, nodes: [], edges: [], selectedId: item.id };
      try {
        const [{ data }] = await Promise.all([
          axios.get('/api/engagement-graph-nodes/graph/', { params: { entity_id: p.id } }),
          this.ensureMetricNames(),
        ]);
        this.detail.nodes = data.nodes || [];
        this.detail.edges = data.edges || [];
      } catch (e) {
        this.detail.error = e?.response?.data?.detail || 'Could not load this item.';
      } finally {
        this.detail.loading = false;
      }
    },
    async ensureMetricNames() {
      if (Object.keys(this.metricNames).length) return;
      try {
        const { data } = await axios.get('/api/impact_analytics/composable-metrics/');
        const list = Array.isArray(data) ? data : (data.results || []);
        const m = {};
        for (const x of list) m[x.id] = x.name;
        this.metricNames = m;
      } catch (e) { /* metric names are optional (only used for initiative story chips) */ }
    },
    setSelected(id) {
      if (id == null) { this.closeDetail(); return; }
      this.detail.selectedId = id;
    },
    closeDetail() {
      this.detail = { open: false, loading: false, error: null, practiceId: null, nodes: [], edges: [], selectedId: null };
    },
    replaceNode(updated) {
      const i = this.detail.nodes.findIndex(n => n.id === updated.id);
      if (i !== -1) this.detail.nodes.splice(i, 1, updated);
      this.syncListItem(updated);
    },
    syncListItem(node) {
      for (const p of this.practices) {
        const it = (p.items || []).find(i => i.id === node.id);
        if (it) { it.prioritized = node.prioritized_at != null; it.prioritized_at = node.prioritized_at; break; }
      }
    },
    async patch(node, payload) {
      this.saving.add(node.id);
      try {
        const { data } = await axios.patch(`/api/engagement-graph-nodes/${node.id}/`, payload);
        this.replaceNode(data);
      } finally {
        this.saving.delete(node.id);
      }
    },
    changeStatus(node, value) {
      if (value === node.status) return;
      this.patch(node, { user_status: value });
    },
    patchField(node, field, value) {
      this.patch(node, { [field]: value });
    },
    patchNotes(node, value) {
      if (value === node.notes) return;
      this.patch(node, { notes: value });
    },
    async acceptLlm(node) {
      this.saving.add(node.id);
      try {
        const { data } = await axios.post(`/api/engagement-graph-nodes/${node.id}/accept-llm-status/`);
        this.replaceNode(data);
      } finally {
        this.saving.delete(node.id);
      }
    },
    async detailTogglePriority(node) {
      this.saving.add(node.id);
      try {
        const data = await setPriority(node.id, node.prioritized_at == null);
        this.replaceNode(data);
      } finally {
        this.saving.delete(node.id);
      }
    },
    onOpenTranscript(engagementId, quote) {
      this.transcript = { open: true, engagementId, quote: quote || '' };
    },
  },
};
</script>
